import Axios from "axios";
import moment from 'moment';

/**
 * @description Extrae solo la parte de fecha (YYYY-MM-DD) de un string ISO/UTC
 * y devuelve un objeto moment en UTC, evitando conversiones de zona horaria
 * que podrían desplazar la fecha un día atrás.
 *
 * Ejemplo: '2024-12-26T00:00:00.000+00:00' → moment.utc('2024-12-26') → 26/12/2024
 * Sin este fix, moment lo convertiría a la TZ local (Chile UTC-3) → 25/12/2024.
 *
 * @param {string} fechaISO - Fecha en formato ISO (ej: '2024-12-26T00:00:00.000+00:00')
 * @returns {moment.Moment|null} Objeto moment en UTC con solo la fecha, o null si la entrada es inválida.
 */
export const parseFechaEmisionUTC = (fechaISO) => {
	if (!fechaISO || typeof fechaISO !== 'string') return null;
	const soloFecha = fechaISO.split('T')[0]; // "2024-12-26"
	const m = moment.utc(soloFecha, 'YYYY-MM-DD', true);
	return m.isValid() ? m : null;
};

export const addMillsSep = (num, moneda = "CLP") => {
    var x1 = (moneda == "CLP") ? Math.round(num).toString() : parseFloat(num).toFixed(2);
    // var x1 = num.toString();
    x1 = x1.replace('.', ',');
    var rgx = /(\d+)(\d{3})/;
    while (rgx.test(x1)) {
        x1 = x1.replace(rgx, '$1' + '.' + '$2');
    }
    return x1;
};

export const currency = value => {
    // let stringValue = value && value.replace(/\./g, '');
    // let valor = stringValue && parseFloat(stringValue);
    // valor = valor && valor.toLocaleString('es-CL');
    // return valor ? valor : '0';
    let newValue = value
        .replace(/[^0-9\,]/g, '')
        .replace(/(\d*\,\d*)(\,)(\d*)/g, '$1$3')
        .replace(/^(\d+,?\d{0,2})\d*$/, '$1');
    let partes = newValue.split(',');
    return addMillsSep(partes[0]);
};

export const compareObjectString = (key, order = 'asc') => {
    return (a, b) => {
        if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) return 0;
        const comparison = a[key].localeCompare(b[key]);

        return order === 'desc' ? comparison * -1 : comparison;
    };
};

const URL_SCRIPT_TOKEN_DRIVE = 'https://script.google.com/macros/s/AKfycbwfc_339gl3N-1vAzDLIaTHbyrZRXRtOjEqcBibgQp5AyQslHE/exec';
const URL_SCRIPT_SUBIDA_DRIVE = 'https://script.google.com/macros/s/AKfycby1hX1I0b8Hy9rZZofEf4WqcRv7vj6Ze0pnMUyNJbv_HqKUtvM/exec';

// Límites de espera para los Apps Script de Drive: si no responden, la promesa se rechaza
// en vez de quedar pendiente para siempre (y con ella el spinner de la pantalla que la espera)
export const TIMEOUT_TOKEN_DRIVE_MS = 60 * 1000;
export const TIMEOUT_DESCARGA_DRIVE_MS = 3 * 60 * 1000;
export const TIMEOUT_SUBIDA_DRIVE_MS = 4 * 60 * 1000;

/**
 * @description Hace un fetch y lee su respuesta como JSON, abortando si todo el proceso
 * (conexión + lectura del cuerpo) supera el tiempo indicado.
 * @param {string} url - URL a consultar.
 * @param {RequestInit} opciones - Opciones de fetch.
 * @param {number} timeoutMs - Tiempo máximo de espera en milisegundos.
 * @returns {Promise<any>} JSON de la respuesta.
 * @throws {Error} Si se agota el tiempo, la respuesta no es 2xx o el cuerpo no es JSON.
 */
export const fetchJsonConTimeout = async (url, opciones, timeoutMs) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, { ...opciones, signal: controller.signal });
        if (!res.ok) throw new Error(`El servicio de archivos respondió con estado ${res.status}`);
        try {
            return await res.json();
        } catch (error) {
            if (error.name === 'AbortError') throw error;
            throw new Error('El servicio de archivos devolvió una respuesta inválida');
        }
    } catch (error) {
        if (error.name === 'AbortError') {
            throw new Error(`El servicio de archivos no respondió en ${Math.round(timeoutMs / 1000)} segundos`);
        }
        throw error;
    } finally {
        clearTimeout(timer);
    }
};

export const downloadUrlAsPromise = async (url, fileName) => {
    const result = await fetchJsonConTimeout(URL_SCRIPT_TOKEN_DRIVE, { method: 'POST' }, TIMEOUT_TOKEN_DRIVE_MS);
    if (!result || !result.access) throw new Error('No se pudo obtener autorización para descargar el archivo');

    return new Promise(function(resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('Authorization', 'Bearer ' + result.access);
        xhr.responseType = 'blob';
        xhr.timeout = TIMEOUT_DESCARGA_DRIVE_MS;
        xhr.onload = function() {
            if (xhr.status === 200) {
                resolve({
                    blob: xhr.response,
                    name: fileName
                });
            } else {
                reject(new Error('Error al descargar el archivo (estado ' + xhr.status + ')'));
            }
        };
        xhr.onerror = function() {
            reject(new Error('Error de red al descargar el archivo'));
        };
        xhr.ontimeout = function() {
            reject(new Error(`La descarga del archivo no terminó en ${Math.round(TIMEOUT_DESCARGA_DRIVE_MS / 1000)} segundos`));
        };
        xhr.send();
    });
};

const leerArchivoComoDataUrl = file => {
    return new Promise(function (resolve, reject) {
        var fileReader = new FileReader();
        fileReader.onload = e => resolve(e.target.result);
        fileReader.onerror = () => reject(new Error('No se pudo leer el archivo ' + (file && file.name ? file.name : '')));
        fileReader.readAsDataURL(file);
    });
};

export const uploadFileAsPromise = async (file, folderId ) => {
    const dataUrl = await leerArchivoComoDataUrl(file);
    var form = new FormData();
    form.append('file', dataUrl.replace(/^.*,/, ''));
    form.append('filename', file.name);
    form.append('folderId', folderId);
    form.append('mimeType', file.type);
    const result = await fetchJsonConTimeout(URL_SCRIPT_SUBIDA_DRIVE, { method: 'POST', body: form }, TIMEOUT_SUBIDA_DRIVE_MS);
    if (!result || !result.id) throw new Error('El servicio de archivos no confirmó la subida del archivo');
    return result;
};

const escaparHtml = texto =>
    String(texto).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * @description Reemplaza el contenido de la pestaña abierta para ver un archivo (la que queda
 * en "/waiting") por una página con el error, para que el usuario no quede esperando indefinidamente.
 * @param {Window|null} pestana - Pestaña devuelta por window.open.
 * @param {Error|string} [error] - Error ocurrido; su mensaje se muestra como detalle.
 */
export const mostrarErrorEnPestana = (pestana, error) => {
    if (!pestana || pestana.closed) return;
    const detalle = error ? (error.message || String(error)) : '';
    const html = '<!doctype html><html lang="es"><head><meta charset="utf-8"><title>No se pudo abrir el archivo</title></head>' +
        '<body style="font-family: Roboto, Arial, sans-serif; padding: 40px; color: #333;">' +
        '<h2>No se pudo abrir el archivo</h2>' +
        '<p>Cierre esta pestaña e intente nuevamente en unos minutos. Si el problema persiste, contacte a soporte.</p>' +
        (detalle ? '<p style="color: #777; font-size: 13px;">Detalle: ' + escaparHtml(detalle) + '</p>' : '') +
        '</body></html>';
    try {
        pestana.location = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    } catch (e) {
        pestana.close();
    }
};

export const getQueryParams = () => {
    const query = window.location.search.substring(1);
    const vars = query.split('&');
    let queryParams = {};
    for (let i = 0; i < vars.length; i++) {
        let pair = vars[i].split('=');
        if (pair.length == 2) queryParams[pair[0]] = pair[1];
    }
    return queryParams;
};


export const checkRut = (rut = '') => {
    if (rut == null) rut = '';
    // Despejar Puntos
    var valor = rut.replace(/\./g, '');
    // Despejar Guión
    valor = valor.replace(/\-/g, '');
    // Aislar Cuerpo y Dígito Verificador
    var cuerpo = valor.slice(0, -1);
    var dv = valor.slice(-1).toUpperCase();
    // Formatear RUN
    rut = cuerpo + '-' + dv;

    if (cuerpo.length < 7) {
        return false;
    }

    var suma = 0;
    var multiplo = 2;

    // Para cada dígito del Cuerpo
    for (var i = 1; i <= cuerpo.length; i++) {
        // Obtener su Producto con el Múltiplo Correspondiente
        var index = multiplo * valor.charAt(cuerpo.length - i);
        // Sumar al Contador General
        suma = suma + index;
        // Consolidar Múltiplo dentro del rango [2,7]
        if (multiplo < 7) {
            multiplo = multiplo + 1;
        } else {
            multiplo = 2;
        }
    }

    // Calcular Dígito Verificador en base al Módulo 11
    var dvEsperado = 11 - (suma % 11);

    // Casos Especiales (0 y K)
    dv = dv == 'K' ? 10 : dv;
    dv = dv == 0 ? 11 : dv;

    // Validar que el Cuerpo coincide con su Dígito Verificador
    if (dvEsperado != dv) {
        return false;
    }

    return true;
};

export const normalizeRut = (rut) => {
    if (!rut) return rut;
    return rut.toUpperCase();
};

/**
 * @description Busca facturas duplicadas en el Gestor de Pagos consultando el RUT
 * en ambas variantes (mayúscula y minúscula del dígito verificador).
 * Esto cubre el caso donde registros históricos fueron guardados con 'k' minúscula.
 * @param {string} numeroFactura
 * @param {string} rut - RUT del proveedor (cualquier capitalización)
 * @param {string} token - Bearer token de autenticación
 * @param {string} apiBaseUrl - URL base de la API de rendiciones
 * @returns {Promise<boolean>} true si ya existe la factura
 */
export const existeFacturaEnGestorPagos = async (numeroFactura, rut, token, apiBaseUrl) => {
    const rutUpper = normalizeRut(rut);
    const buildUrl = (rutVariant) =>
        `${apiBaseUrl}/facturas/?numero_factura_eq=${encodeURIComponent(numeroFactura)}&proveedor.rut_eq=${encodeURIComponent(rutVariant)}&eliminado_eq=false`;

    const headers = { Authorization: `Bearer ${token}` };

    const resUpper = await fetch(buildUrl(rutUpper), { method: 'GET', headers });
    const dataUpper = await resUpper.json();
    if (dataUpper?.data && dataUpper.data.length > 0) return true;

    // Solo hace segunda consulta si el dígito verificador es letra (K/k)
    if (/[kK](-?)$/.test(rut.trim())) {
        const rutLower = rut.trim().replace(/K$/i, 'k');
        const resLower = await fetch(buildUrl(rutLower), { method: 'GET', headers });
        const dataLower = await resLower.json();
        if (dataLower?.data && dataLower.data.length > 0) return true;
    }

    return false;
};

export const formatoRut = (rut) => {
    var textoRut = rut.replace(/[\W\s\._\-a-jA-Jl-zL-Z]+/g, '');
    var fullRut = '';

    if (textoRut.length <= 1) {
        if (textoRut == 'k') {
            fullRut = 'K';
        } else {
            fullRut = textoRut;
        }
    } else {
        var cont = 0;
        for (var i = textoRut.length - 1; i >= 0; i--) {
            if (i == textoRut.length - 1) {
                if (textoRut.charAt(i) == 'k') {
                    fullRut = '-K';
                } else {
                    fullRut = '-' + textoRut.charAt(i);
                }
            } else {
                if (cont == 3) {
                    fullRut = '.' + fullRut;
                    cont = 0;
                }
                fullRut = textoRut.charAt(i) + fullRut;
                cont++;
            }
        }
    }
    return fullRut;
};

export const uploadFileToCloudStorage = async (file, route, name, bucketName) => {
	try {
		var formData = new FormData();
		formData.append("folder", route);
		formData.append("bucket", bucketName);
		formData.append("name", name);
		formData.append("file", file);

		let upload = await Axios.post(`${process.env.REACT_APP_STORAGE_URL || 'https://storage.cydingenieria.com'}/upload`, formData);
		if (!Boolean(upload.status === 201 || upload.status === 200)) {
			throw new Error("Error al intentar subir el archivo.");
		}
		let uploadedFile = {
			mime_type: upload.data.content_type,
			nombre: name,
			// repositorio: `${upload.data.bucket}/${route}`,
            path: `${upload.data.bucket}/${route}`,
            repositorio: 'Cloud Storage',
			url: upload.data.pathname,
		}
		return uploadedFile;
	} catch (error) {
		console.error(error);
		throw error;
	}
}

/**
 * @description Construye el objeto `archivo_factura` para enviar a Gestor de Pagos a partir
 * de la URL pública del PDF en Facele (que ya reside en Cloud Storage).
 *
 * @param {string} urlPdfFacele - URL completa del PDF (ej: https://storage.googleapis.com/bucket/path/file.pdf).
 * @returns {object|null} Objeto con mime_type, nombre, path, repositorio y url; o null si la URL no es válida.
 */
export const resolveArchivoStorageFacele = (urlPdfFacele) => {
	if (!urlPdfFacele) {
		console.warn('[resolveArchivoStorageFacele] url_pdf no disponible, se retorna null.');
		return null;
	}
	const urlObj = new URL(urlPdfFacele);
	const pathParts = urlObj.pathname.split('/').filter(Boolean);
	const bucket = pathParts[0];
	const fileName = pathParts[pathParts.length - 1];
	const route = pathParts.slice(1, -1).join('/');
	return {
		mime_type: 'application/pdf',
		nombre: fileName,
		path: `${bucket}/${route}`,
		repositorio: 'Cloud Storage',
		url: urlPdfFacele,
	};
};
